import type { ReviewStatus, UserNotice } from "../notices/types";
import type { BrowserDatabase, SettingRecord } from "./schema";

export const USER_BACKUP_FORMAT = "ai-support-monitor-user-backup";
export const USER_BACKUP_SCHEMA_VERSION = 1;
export const USER_BACKUP_SETTING_KEYS = [
  "defaultFilters",
  "defaultSort",
  "reportPreferences",
  "companyProfile",
] as const;

export type RestoreMode = "merge" | "replace";

export interface UserBackupPayload {
  userNotices: UserNotice[];
  settings: SettingRecord[];
}

export interface UserBackupDocument {
  format: typeof USER_BACKUP_FORMAT;
  schemaVersion: number;
  createdAt: string;
  contentHash: string;
  payload: UserBackupPayload;
}

export interface CreatedUserBackup {
  document: UserBackupDocument;
  json: string;
  fileName: string;
}

export interface RestoreUserBackupOptions {
  mode?: RestoreMode;
  now?: Date;
  crypto?: Crypto;
}

export interface RestoreUserBackupResult {
  mode: RestoreMode;
  restoredCount: number;
  preRestoreBackup: CreatedUserBackup;
}

export interface BackupAdvisory {
  lastBackupAt: string | null;
  daysSinceBackup: number | null;
  warningRequired: boolean;
  deletionRisk: string;
}

export class BackupValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BackupValidationError";
  }
}

export class BackupSchemaVersionError extends BackupValidationError {
  readonly receivedVersion: number;

  constructor(receivedVersion: number) {
    super(
      `Backup schema version ${receivedVersion} is newer than supported version ${USER_BACKUP_SCHEMA_VERSION}.`,
    );
    this.name = "BackupSchemaVersionError";
    this.receivedVersion = receivedVersion;
  }
}

export class BackupHashMismatchError extends BackupValidationError {
  constructor() {
    super("Backup content hash is invalid.");
    this.name = "BackupHashMismatchError";
  }
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function assert(condition: unknown, message: string): asserts condition {
  if (!condition) throw new BackupValidationError(message);
}

function isIsoDate(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    !Number.isNaN(Date.parse(value))
  );
}

function assertUserNotice(value: unknown, index: number): asserts value is UserNotice {
  const path = `payload.userNotices[${index}]`;
  assert(isRecord(value), `${path} must be an object.`);
  assert(typeof value.noticeId === "string" && value.noticeId.length > 0, `${path}.noticeId is required.`);
  assert(
    Array.isArray(value.sourceNoticeIds) &&
      value.sourceNoticeIds.every((item) => typeof item === "string"),
    `${path}.sourceNoticeIds must be a string array.`,
  );
  assert(typeof value.bookmarked === "boolean", `${path}.bookmarked must be boolean.`);
  assert(
    ["미검토", "검토중", "문의필요", "제안준비", "보류", "제외"].includes(
      String(value.reviewStatus),
    ),
    `${path}.reviewStatus is invalid.`,
  );
  assert(typeof value.assignee === "string", `${path}.assignee must be a string.`);
  assert(typeof value.note === "string", `${path}.note must be a string.`);
  assert(typeof value.hidden === "boolean", `${path}.hidden must be boolean.`);
  assert(
    value.lastOpenedAt === null || isIsoDate(value.lastOpenedAt),
    `${path}.lastOpenedAt is invalid.`,
  );
  assert(isIsoDate(value.createdAt), `${path}.createdAt is invalid.`);
  assert(isIsoDate(value.updatedAt), `${path}.updatedAt is invalid.`);
}

function assertSetting(value: unknown, index: number): asserts value is SettingRecord {
  const path = `payload.settings[${index}]`;
  assert(isRecord(value), `${path} must be an object.`);
  assert(
    typeof value.key === "string" &&
      (USER_BACKUP_SETTING_KEYS as readonly string[]).includes(value.key),
    `${path}.key is not a restorable user setting.`,
  );
  assert(isIsoDate(value.updatedAt), `${path}.updatedAt is invalid.`);
  assert("value" in value, `${path}.value is required.`);
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const object = value as UnknownRecord;
  return `{${Object.keys(object)
    .sort()
    .filter((key) => object[key] !== undefined)
    .map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`)
    .join(",")}}`;
}

function defaultCrypto(): Crypto {
  if (!globalThis.crypto?.subtle) {
    throw new BackupValidationError("Web Crypto SHA-256 is unavailable.");
  }
  return globalThis.crypto;
}

async function sha256Hex(value: string, cryptoProvider = defaultCrypto()): Promise<string> {
  const digest = await cryptoProvider.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function computeUserBackupHash(
  payload: UserBackupPayload,
  cryptoProvider?: Crypto,
): Promise<string> {
  return sha256Hex(stableStringify(payload), cryptoProvider);
}

export function formatUserBackupFileName(date = new Date()): string {
  const twoDigits = (value: number) => String(value).padStart(2, "0");
  return [
    "ai-support-monitor-backup-",
    date.getFullYear(),
    twoDigits(date.getMonth() + 1),
    twoDigits(date.getDate()),
    "-",
    twoDigits(date.getHours()),
    twoDigits(date.getMinutes()),
    ".json",
  ].join("");
}

function normalizeUserNotices(records: readonly UserNotice[]): UserNotice[] {
  return records
    .map((record) => ({
      ...record,
      sourceNoticeIds: [...new Set(record.sourceNoticeIds)].sort(),
    }))
    .sort((left, right) => left.noticeId.localeCompare(right.noticeId));
}

export async function createUserDataBackup(
  database: BrowserDatabase,
  options: { now?: Date; crypto?: Crypto } = {},
): Promise<CreatedUserBackup> {
  const createdAtDate = options.now ?? new Date();
  const createdAt = createdAtDate.toISOString();
  const payload = await database.transaction(
    "r",
    database.userNotices,
    database.settings,
    async (): Promise<UserBackupPayload> => {
      const [userNotices, allSettings] = await Promise.all([
        database.userNotices.toArray(),
        database.settings.toArray(),
      ]);
      const allowedSettings = new Set<string>(USER_BACKUP_SETTING_KEYS);
      return {
        userNotices: normalizeUserNotices(userNotices),
        settings: allSettings
          .filter((setting) => allowedSettings.has(setting.key))
          .sort((left, right) => left.key.localeCompare(right.key)),
      };
    },
  );
  const contentHash = await computeUserBackupHash(payload, options.crypto);
  const document: UserBackupDocument = {
    format: USER_BACKUP_FORMAT,
    schemaVersion: USER_BACKUP_SCHEMA_VERSION,
    createdAt,
    contentHash,
    payload,
  };
  return {
    document,
    json: `${JSON.stringify(document, null, 2)}\n`,
    fileName: formatUserBackupFileName(createdAtDate),
  };
}

export async function parseAndValidateUserBackup(
  input: string | unknown,
  cryptoProvider?: Crypto,
): Promise<UserBackupDocument> {
  let parsed: unknown = input;
  if (typeof input === "string") {
    try {
      parsed = JSON.parse(input) as unknown;
    } catch {
      throw new BackupValidationError("Backup is not valid JSON.");
    }
  }

  assert(isRecord(parsed), "Backup must be an object.");
  assert(parsed.format === USER_BACKUP_FORMAT, "Backup format is unsupported.");
  assert(Number.isInteger(parsed.schemaVersion), "Backup schemaVersion must be an integer.");
  if (Number(parsed.schemaVersion) > USER_BACKUP_SCHEMA_VERSION) {
    throw new BackupSchemaVersionError(Number(parsed.schemaVersion));
  }
  assert(
    parsed.schemaVersion === USER_BACKUP_SCHEMA_VERSION,
    `Backup schema version ${String(parsed.schemaVersion)} is unsupported.`,
  );
  assert(isIsoDate(parsed.createdAt), "Backup createdAt is invalid.");
  assert(
    typeof parsed.contentHash === "string" && /^[a-f0-9]{64}$/i.test(parsed.contentHash),
    "Backup contentHash is invalid.",
  );
  assert(isRecord(parsed.payload), "Backup payload must be an object.");
  assert(Array.isArray(parsed.payload.userNotices), "Backup userNotices must be an array.");
  assert(Array.isArray(parsed.payload.settings), "Backup settings must be an array.");
  parsed.payload.userNotices.forEach(assertUserNotice);
  parsed.payload.settings.forEach(assertSetting);

  const notices = parsed.payload.userNotices as UserNotice[];
  assert(
    new Set(notices.map((notice) => notice.noticeId)).size === notices.length,
    "Backup contains duplicate user notice IDs.",
  );
  const settings = parsed.payload.settings as SettingRecord[];
  assert(
    new Set(settings.map((setting) => setting.key)).size === settings.length,
    "Backup contains duplicate setting keys.",
  );

  const document = parsed as unknown as UserBackupDocument;
  const actualHash = await computeUserBackupHash(document.payload, cryptoProvider);
  if (actualHash !== document.contentHash.toLowerCase()) {
    throw new BackupHashMismatchError();
  }
  return document;
}

function mergeUserRecord(current: UserNotice | undefined, incoming: UserNotice): UserNotice {
  if (!current) return incoming;
  const preferred = incoming.updatedAt >= current.updatedAt ? incoming : current;
  return {
    ...preferred,
    sourceNoticeIds: [
      ...new Set([...current.sourceNoticeIds, ...incoming.sourceNoticeIds]),
    ].sort(),
    createdAt:
      current.createdAt <= incoming.createdAt ? current.createdAt : incoming.createdAt,
  };
}

export async function restoreUserDataBackup(
  database: BrowserDatabase,
  input: string | unknown,
  options: RestoreUserBackupOptions = {},
): Promise<RestoreUserBackupResult> {
  // Validation and hash verification happen before the pre-restore snapshot or
  // any write transaction, so a corrupt upload cannot mutate local state.
  const backup = await parseAndValidateUserBackup(input, options.crypto);
  const preRestoreBackup = await createUserDataBackup(database, {
    now: options.now,
    crypto: options.crypto,
  });
  const mode = options.mode ?? "merge";
  assert(mode === "merge" || mode === "replace", "Restore mode is invalid.");
  const restorableKeys = new Set<string>(USER_BACKUP_SETTING_KEYS);

  await database.transaction(
    "rw",
    database.userNotices,
    database.settings,
    async () => {
      let restoredNotices = backup.payload.userNotices;
      if (mode === "replace") {
        await database.userNotices.clear();
        await database.settings.bulkDelete([...restorableKeys]);
      } else {
        const current = new Map(
          (await database.userNotices.toArray()).map((record) => [record.noticeId, record]),
        );
        restoredNotices = backup.payload.userNotices.map((record) =>
          mergeUserRecord(current.get(record.noticeId), record),
        );
      }

      await database.userNotices.bulkPut(normalizeUserNotices(restoredNotices));
      await database.settings.bulkPut(
        backup.payload.settings.filter((setting) => restorableKeys.has(setting.key)),
      );
    },
  );

  return {
    mode,
    restoredCount: backup.payload.userNotices.length,
    preRestoreBackup,
  };
}

export async function markUserBackupDownloaded(
  database: BrowserDatabase,
  timestamp = new Date().toISOString(),
): Promise<void> {
  await database.settings.put({
    key: "lastBackupAt",
    value: timestamp,
    updatedAt: timestamp,
  });
}

export async function getBackupAdvisory(
  database: BrowserDatabase,
  now = new Date(),
  warningAfterDays = 30,
): Promise<BackupAdvisory> {
  const value = (await database.settings.get("lastBackupAt"))?.value;
  const lastBackupAt = typeof value === "string" && isIsoDate(value) ? value : null;
  const elapsed = lastBackupAt ? now.getTime() - new Date(lastBackupAt).getTime() : null;
  const daysSinceBackup = elapsed === null ? null : Math.max(0, Math.floor(elapsed / 86_400_000));
  return {
    lastBackupAt,
    daysSinceBackup,
    warningRequired: daysSinceBackup === null || daysSinceBackup >= warningAfterDays,
    deletionRisk:
      "브라우저 또는 이 사이트의 데이터를 삭제하면 관심 공고와 메모가 사라질 수 있습니다.",
  };
}
